import type { HeaderService, ListItem } from '@patsnap-utils/biz';
import { Site, SLD } from '@patsnap-utils/biz';
import { getIsOverseas } from '../site-region';
import '@patsnap-ui/icon/assets/solid/HistoryRecord.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/AI.svg';

type ILocale = 'cn' | 'en' | 'tw' | 'jp'

export interface EurekaAmountsControlForAvatarMenu {
    credit_package?: boolean;
    count_package?: boolean;
}

export interface PackageForAvatarMenu {
    package_type?: string;
}

export interface CustomLinksOptions {
    isPersonal: boolean;
    isEnterprise: boolean;
    locale: ILocale;
    packages?: PackageForAvatarMenu[];
    packagesLoaded?: boolean;
    control?: EurekaAmountsControlForAvatarMenu | null;
}

const MANAGED_MENU_KEYS = ['credits', 'package', 'version', 'manage-subscription'];
const MANAGED_MENU_PATHNAMES = ['credits', 'user-setting/amount', 'user-setting/package/ip', 'user-setting/package/management'];
const PERSONAL_HIDDEN_MENU_KEYS = ['settings', 'team'];

function getCreditsMenuHref() {
    return new Site({ sld: SLD.EUREKA, pathname: 'credits' }).href;
}

function normalizeMenuPath(href: string) {
    try {
        const url = new URL(href, 'https://eureka.local');
        const hashPath = url.hash.replace(/^#\/?/, '').split('?')[0];
        const path = hashPath || url.pathname;
        return path.replace(/^\/+|\/+$/g, '');
    } catch {
        return href.split('?')[0].replace(/^\/+|\/+$/g, '');
    }
}

function isManagedMenuItem(item: ListItem) {
    const key = item.key as string | undefined;
    if (key && MANAGED_MENU_KEYS.includes(key)) {
        return true;
    }

    if (!item.href) {
        return false;
    }

    return MANAGED_MENU_PATHNAMES.includes(normalizeMenuPath(item.href));
}

function getBaseItems(items: ListItem[]) {
    return items.filter(item => !isManagedMenuItem(item));
}

function filterPersonalHiddenItems(items: ListItem[]) {
    return items.filter(item => !PERSONAL_HIDDEN_MENU_KEYS.includes(item.key as string));
}

function hasCommercialPackage(packages?: PackageForAvatarMenu[]) {
    return packages?.some(item => item.package_type === 'COMMERCIAL') === true;
}

export function customProductsLinkInUserCenter(headerService: HeaderService, options: CustomLinksOptions) {
    const buildin = headerService.userCenterService.beforeRenderMenuItems;

    headerService.userCenterService.beforeRenderMenuItems = function beforeRenderMenuItems(items: ListItem[]) {
        const labels = {
            allVersions: 'CustomHeader.allVersions',
            credits: 'CustomHeader.credits',
            history: 'CustomHeader.history',
            manageSubscription: 'CustomHeader.manageSubscription',
            usage: 'CustomHeader.usage',
        };
        const baseItems = getBaseItems(buildin(items));
        baseItems.splice(Math.min(1, baseItems.length), 0, {
            href: new Site({ sld: SLD.EUREKA, pathname: 'history' }).href,
            i18n: labels.history,
            icon: 'SolidHistoryRecord',
            key: 'history',
        });

        const managedItems: ListItem[] = [];
        if (options.control?.credit_package === true) {
            managedItems.push({
                href: getCreditsMenuHref(),
                i18n: labels.credits,
                icon: 'Credits',
                key: 'credits',
            });
        }

        if (options.isEnterprise) {
            managedItems.push({
                href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/package/ip' }).href,
                i18n: labels.allVersions,
                icon: 'SolidRocket',
                key: 'version',
            });
            if (options.control?.count_package === true) {
                managedItems.push({
                    href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/amount' }).href,
                    i18n: labels.usage,
                    icon: 'Userpackage',
                    key: 'package',
                });
            }
        } else if (options.isPersonal) {
            if (!getIsOverseas()) {
                managedItems.push({
                    href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/package/ip' }).href,
                    i18n: labels.allVersions,
                    icon: 'SolidRocket',
                    key: 'version',
                });
            }
            if (options.packagesLoaded === true && hasCommercialPackage(options.packages)) {
                managedItems.push({
                    href: new Site({ sld: SLD.EUREKA, pathname: 'user-setting/package/management' }).href,
                    i18n: labels.manageSubscription,
                    icon: 'SolidRocket',
                    key: 'manage-subscription',
                });
            }
        }

        const visibleBaseItems = options.isPersonal ? filterPersonalHiddenItems(baseItems) : baseItems;
        return [...managedItems, ...visibleBaseItems];
    };
}
